import React from "react";
import { EventCard } from "@/components/rider/EventCard";
import { Button } from "@/components/ui/button";
import { CommunityEvent } from "@/types/rider";
import { Calendar, ArrowLeft, Clock, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BreakawayDallasVehicleOption, BreakawayDallasDaySelection } from "@/pages/RiderEventSelectBreakawayDallas";

interface DesktopEventSelectorBreakawayDallasProps {
  event: CommunityEvent;
  eventSelected: boolean;
  selectedDay: BreakawayDallasDaySelection;
  selectedVehicleId: string | null;
  vehicleOptions: BreakawayDallasVehicleOption[];
  dayDates: Record<BreakawayDallasDaySelection, string>;
  availableDays: BreakawayDallasDaySelection[];
  onEventSelect: () => void;
  onBackToEvent: () => void;
  onDaySelect: (day: BreakawayDallasDaySelection) => void;
  onVehicleSelect: (vehicleId: string) => void;
  onContinue: () => void;
  onManualEntry: () => void;
}

export const DesktopEventSelectorBreakawayDallas = ({
  event,
  eventSelected,
  selectedDay,
  selectedVehicleId,
  vehicleOptions,
  dayDates,
  availableDays,
  onEventSelect,
  onBackToEvent,
  onDaySelect,
  onVehicleSelect,
  onContinue,
  onManualEntry
}: DesktopEventSelectorBreakawayDallasProps) => {
  const selectedVehicle = vehicleOptions.find(v => v.id === selectedVehicleId);
  const navigate = useNavigate();

  return (
    <div className="hidden md:block container mx-auto px-4 py-6 max-w-7xl">
      <div className="flex justify-between items-center mb-8">
        <button 
          onClick={eventSelected ? onBackToEvent : () => navigate('/')}
          className="flex items-center text-black hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="mr-1 h-5 w-5" />
          <span>{eventSelected ? 'Back to Event' : 'Book A Custom Trip'}</span>
        </button>
        <h1 className="text-3xl font-bold text-center text-black">Book Your Ride To</h1>
        <div className="w-32"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 mt-8">
        {/* Left Side - Event Card */}
        <div className="flex items-center justify-center">
          <motion.div 
            className={`w-[350px] cursor-pointer transition-all duration-300 ${eventSelected ? 'scale-90 opacity-75' : ''}`}
            whileHover={!eventSelected ? { scale: 1.02 } : {}}
            whileTap={!eventSelected ? { scale: 0.98 } : {}}
            onClick={onEventSelect}
          >
            <EventCard 
              event={event} 
              isSelected={eventSelected} 
              onClick={onEventSelect}
            />
          </motion.div>
        </div>
        
        {/* Right Side - Details Panel */}
        <AnimatePresence mode="wait">
          {eventSelected ? (
            <motion.div
              key="details"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <h2 className="text-3xl font-bold text-black mb-4">{event.name}</h2>
              
              {/* Day Selector - Only Friday and Saturday */}
              <div className="mb-6">
                <p className="text-gray-600 mb-3 flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  Select Your Day
                </p>
                <div className="flex gap-3">
                  {availableDays.map((day) => (
                    <button
                      key={day}
                      onClick={() => onDaySelect(day)}
                      className={`flex-1 py-3 px-4 rounded-full font-medium transition-all ${
                        selectedDay === day
                          ? 'bg-black text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </button>
                  ))}
                </div>
                <p className="text-center text-gray-500 mt-2">{dayDates[selectedDay]}</p>
              </div>

              <div className="border-t border-gray-200 my-4 pt-4">
                <h3 className="text-xl font-semibold mb-4 text-black">Choose Your Option</h3>
                
                {/* Vehicle Options - 6 options with scroll */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {vehicleOptions.map((vehicle) => (
                    <motion.div
                      key={vehicle.id}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div 
                        className={`cursor-pointer transition-all duration-200 border rounded-xl p-4 ${
                          selectedVehicleId === vehicle.id
                            ? 'border-black bg-gray-50 shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }`}
                        onClick={() => onVehicleSelect(vehicle.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="text-3xl">{vehicle.icon}</div>
                            <div>
                              <h4 className="font-semibold text-black text-lg">{vehicle.name}</h4>
                              <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                                <span className="flex items-center gap-1">
                                  <Users className="h-4 w-4" />
                                  {vehicle.capacity === 1 ? '1 person' : `${vehicle.capacity} people`}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  {vehicle.durationHours}hr
                                </span>
                              </div>
                              <span className={`inline-block mt-2 text-xs px-3 py-1 rounded-full ${
                                vehicle.isRoundTrip 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {vehicle.isRoundTrip ? 'Round Trip' : 'One Way'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-black">${vehicle.price}</div>
                            {vehicle.capacity > 1 && (
                              <p className="text-sm text-gray-500">${Math.round(vehicle.price / vehicle.capacity)}/person</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Summary & Book Button */}
              {selectedVehicleId && selectedVehicle && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 border-t border-gray-200 pt-6"
                >
                  <div className="flex justify-between items-center mb-4 border border-gray-200 rounded-lg p-4">
                    <div>
                      <p className="text-2xl font-bold text-black">${selectedVehicle.price}</p>
                      <p className="text-gray-500">Total Price</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-black">{dayDates[selectedDay]}</p>
                      <p className="text-gray-500">{selectedVehicle.isRoundTrip ? 'Round Trip' : 'One Way'}</p>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full bg-black text-white hover:bg-gray-800 rounded-full py-6 text-lg"
                    onClick={onContinue}
                  >
                    Book Today 🚌
                  </Button>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="placeholder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center h-full"
            >
              <div className="text-center">
                <p className="text-xl text-gray-500 mb-4">Tap the event card to see options</p>
                <p className="text-gray-400">Shuttles & Party Buses available</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      <div className="flex justify-center pb-8">
        <button 
          onClick={onManualEntry}
          className="text-black underline hover:text-gray-600 transition-colors text-lg"
        >
          Enter Trip Request Manually
        </button>
      </div>
    </div>
  );
};
